import { CheckCircle2, Copy, Loader2, XCircle } from "lucide-react";
import type { JSX } from "react";

import { useDomainDkim } from "../adminQueries.js";
import { dnsHostLabel } from "../dnsHost.js";

/**
 * DKIM-Einträge einer bestätigten Domain. Der Mailserver unterschreibt jede Nachricht;
 * ohne den passenden Schlüssel im DNS scheitert die Prüfung beim Empfänger, und mit
 * DMARC landet die Post im Spam.
 */
export function DomainDkim({ domainId, domain }: { domainId: string; domain: string }): JSX.Element | null {
  const records = useDomainDkim(domainId, true);
  if (records.isPending) return <p className="flex items-center gap-2 border-t border-line p-4 text-xs text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />DKIM-Schlüssel werden geladen …</p>;
  if (records.isError) return <p role="alert" className="border-t border-line p-4 text-sm text-danger">DKIM-Schlüssel nicht abrufbar: {records.error.message}</p>;
  if (records.data.length === 0) return null;
  return (
    <div className="border-t border-line">
      <p className="px-4 pt-4 text-xs text-ink-muted">Damit eure Mails nicht im Spam landen, gehören diese Schlüssel ins DNS. Gmail prüft den RSA-Schlüssel; der zweite schadet nicht.</p>
      <ul className="divide-y divide-line">
        {records.data.map((record) => (
          <li key={record.name} className="flex gap-3 p-4">
            {record.published ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-label="Im DNS gefunden" /> : <XCircle className="h-5 w-5 shrink-0 text-danger" aria-label="Fehlt im DNS" />}
            <span className="min-w-0 flex-1">
              <b className="block text-sm text-ink">E-Mail-Unterschrift (DKIM, {record.keyType === "rsa" ? "RSA" : "Ed25519"})</b>
              <code className="mt-1 block break-all text-xs text-ink-muted">{record.type} · {record.name}<br />Host: {dnsHostLabel(record.name, domain)}<br />{record.value}</code>
              {record.published ? null : <span className="mt-1 block text-xs text-danger">Noch nicht im DNS gefunden.</span>}
            </span>
            <button type="button" title="Wert kopieren" onClick={() => void navigator.clipboard.writeText(record.value)} className="h-8 p-2 text-ink-muted"><Copy className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
